import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

/**
 * Connection state shown in the status bar (ARCHITECTURE.md, "Connection states").
 * - storeServer: can this device reach the store backend? If not, orders queue on the device.
 * - ho: can the store backend reach HO? If not, only HO-only features are disabled.
 */
export interface ConnectionState {
  storeServer: 'reachable' | 'unreachable';
  ho: 'online' | 'offline';
  /** Store outbox rows not yet at HO, plus orders queued on this device. */
  pendingSync: number;
  setStoreServer(state: ConnectionState['storeServer']): void;
  setHo(state: ConnectionState['ho']): void;
  setPendingSync(count: number): void;
}

export const connectionStore = createStore<ConnectionState>((set) => ({
  storeServer: 'reachable',
  ho: 'online',
  pendingSync: 0,
  setStoreServer: (storeServer) => set({ storeServer }),
  setHo: (ho) => set({ ho }),
  setPendingSync: (pendingSync) => set({ pendingSync }),
}));

export function useConnection<T>(selector: (state: ConnectionState) => T): T {
  return useStore(connectionStore, selector);
}

/** Maps connection state to the status bar indicator. */
export function syncStateOf(state: Pick<ConnectionState, 'storeServer' | 'ho' | 'pendingSync'>) {
  if (state.storeServer === 'unreachable') return 'store-unreachable' as const;
  if (state.ho === 'offline') return 'offline-ho' as const;
  return state.pendingSync > 0 ? ('pending' as const) : ('synced' as const);
}
